"use client";

import { useEffect } from "react";

import { jsErrorFieldsFromUnknown, reportJsError } from "@/lib/js-error-beacon";

// Installs window-level JS-error handlers that feed the owned `js_error` beacon
// (events_raw, rule 34 — edge only, never a backend route). Captures the two
// classes a React ErrorBoundary can't see: synchronous uncaught errors
// (`window.onerror`) and unhandled promise rejections
// (`window.onunhandledrejection`). The ErrorBoundary covers React render errors.
// Throttle + dedupe live in js-error-beacon.ts so a loop can't flood the sink.
export default function GlobalErrorReporter() {
  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const onError = (event: ErrorEvent) => {
      try {
        const fromError = event.error
          ? jsErrorFieldsFromUnknown(event.error)
          : {
              error_message: event.message || "Uncaught error",
              error_name: "Error",
              error_stack: "",
            };
        reportJsError({
          ...fromError,
          boundary: "window.onerror",
          // event.filename:line:col is the only location info when there is no
          // Error object (cross-origin "Script error.") — keep it in the stack.
          error_stack:
            fromError.error_stack ||
            (event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : ""),
        });
      } catch {
        // never let the reporter throw
      }
    };

    const onRejection = (event: PromiseRejectionEvent) => {
      try {
        reportJsError({
          ...jsErrorFieldsFromUnknown(event.reason),
          boundary: "window.onunhandledrejection",
        });
      } catch {
        // never let the reporter throw
      }
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return null;
}
